import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import Loading from '../lib/Loading';
import { fetchSearchResults } from '../services/SearchService';
import { DIVISIONS_TRANSFORM, GENDER_TRANSFORM } from '../utils/constants';
import { Input } from './ui/input';
import { Button } from './ui/button';

interface SearchProps {
    // Unused -- kept for compatibility. Nav.jsx renders `<Search isResultsPanel={false} />`
    // and a commented-out `/results` route was going to pass `true`; the original
    // component destructured this prop but never referenced it either.
    isResultsPanel?: boolean;
}

interface SearchResults {
    people: any[];
    teams: any[];
    tournaments: any[];
}

function ResultGroup({
    title,
    children
}: {
    title: string;
    children: React.ReactNode;
}) {
    return (
        <div className="border-t first:border-t-0">
            <div className="px-4 pt-3 pb-1 text-sm font-semibold text-muted-foreground">{title}</div>
            {children}
        </div>
    );
}

function ResultRow({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onClick();
            }}
            className="cursor-pointer px-4 py-2 hover:bg-accent hover:text-accent-foreground">
            {children}
        </div>
    );
}

function SearchPanel({
    loading,
    results,
    open,
    onNavigate
}: {
    loading: boolean;
    results: SearchResults | undefined;
    open: boolean;
    onNavigate: (path: string, name: string) => void;
}) {
    if (!open || (!loading && results === undefined)) {
        return null;
    }

    const hasResults =
        results && (results.people.length || results.teams.length || results.tournaments.length);

    return (
        <div className="absolute top-full left-0 z-20 mt-2 w-full min-w-[300px] rounded-md border bg-popover text-popover-foreground shadow-lg">
            {loading ? (
                <Loading />
            ) : !hasResults ? (
                <div className="flex h-24 items-center justify-center">
                    <span className="text-sm text-muted-foreground">No results</span>
                </div>
            ) : (
                <div className="max-h-[70vh] overflow-y-auto py-2">
                    {results!.teams.length ? (
                        <ResultGroup title="Teams">
                            {results!.teams.map((t) => (
                                <ResultRow key={t.slug} onClick={() => onNavigate(`/team/${t.slug}`, t.name)}>
                                    <div>
                                        {t.name} {t.nickname || ''}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {DIVISIONS_TRANSFORM[t.division as keyof typeof DIVISIONS_TRANSFORM]}{' '}
                                        {GENDER_TRANSFORM[t.gender as keyof typeof GENDER_TRANSFORM]}
                                    </div>
                                </ResultRow>
                            ))}
                        </ResultGroup>
                    ) : null}
                    {results!.tournaments.length ? (
                        <ResultGroup title="Tournaments">
                            {results!.tournaments.map((t) => (
                                <ResultRow
                                    key={t.slug}
                                    onClick={() => onNavigate(`/tournament/${t.slug}`, t.name)}>
                                    <div>{t.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {DIVISIONS_TRANSFORM[t.division as keyof typeof DIVISIONS_TRANSFORM]}{' '}
                                        {GENDER_TRANSFORM[t.gender as keyof typeof GENDER_TRANSFORM]}
                                    </div>
                                </ResultRow>
                            ))}
                        </ResultGroup>
                    ) : null}
                    {results!.people.length ? (
                        <ResultGroup title="People">
                            {results!.people.map((p) => (
                                <ResultRow key={p.slug} onClick={() => onNavigate(`/person/${p.slug}`, p.name)}>
                                    {p.name}
                                </ResultRow>
                            ))}
                        </ResultGroup>
                    ) : null}
                </div>
            )}
        </div>
    );
}

export default function Search(_props: SearchProps) {
    const navigate = useNavigate();
    const [value, setValue] = useState('');
    const [debouncedValue, setDebouncedValue] = useState('');
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLFormElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    useEffect(() => () => clearTimeout(debounceTimer.current), []);

    useEffect(() => {
        clearTimeout(debounceTimer.current);
        if (value && open) {
            debounceTimer.current = setTimeout(() => setDebouncedValue(value), 500);
        } else {
            setDebouncedValue('');
        }
    }, [value, open]);

    const searchQuery = useQuery({
        queryKey: ['search', debouncedValue],
        queryFn: () => fetchSearchResults({ input: debouncedValue, pageSize: 12 }),
        enabled: open && Boolean(debouncedValue)
    });

    // Mirrors the original: the spinner shows as soon as the user types (while
    // the 500ms debounce is still pending), not just while the request is in flight.
    const awaitingDebounce = open && Boolean(value) && value !== debouncedValue;
    const loading = awaitingDebounce || (open && Boolean(debouncedValue) && searchQuery.isFetching);
    const results = open && debouncedValue ? searchQuery.data : undefined;

    const closeAndNavigate = (path: string, name: string) => {
        navigate(path);
        setValue(name);
        setOpen(false);
    };

    const clearButtonClick = () => {
        setValue('');
        inputRef.current?.focus();
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, []);

    return (
        <form
            ref={wrapperRef}
            onSubmit={(e) => e.preventDefault()}
            noValidate
            autoComplete="off"
            className="relative w-full">
            <div className="relative">
                <Input
                    ref={inputRef}
                    id="searchInput"
                    aria-label="Search"
                    onFocus={() => setOpen(true)}
                    placeholder="Enter a team, tournament, or player name"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className={value ? 'pr-9' : undefined}
                />
                {value ? (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Clear search"
                        onClick={clearButtonClick}
                        className="absolute top-1/2 right-1 -translate-y-1/2">
                        <X className="h-4 w-4" />
                    </Button>
                ) : null}
            </div>
            <SearchPanel loading={loading} results={results} open={open} onNavigate={closeAndNavigate} />
        </form>
    );
}
